'use client'

import { useState, useTransition } from 'react'
import { Star } from 'lucide-react'
import { cn } from '@/lib/utils'
import { rateSong } from '@/app/songlist/actions'

interface StarRatingProps {
    songId: string
    currentRating?: number
    averageRating: number
    totalRatings: number
    userId?: string
    readOnly?: boolean
}

export default function StarRating({ 
  songId, 
  currentRating = 0, 
  averageRating = 0,
  totalRatings = 0,
  userId,
  readOnly = false
}: StarRatingProps) {
  const [hoverValue, setHoverValue] = useState(0)
  const [localRating, setLocalRating] = useState(currentRating)
  const [isPending, startTransition] = useTransition()

  async function handleRate(value: number) {
      if (readOnly || !userId) return
      
      setLocalRating(value)
      startTransition(async () => {
          await rateSong(songId, value, userId)
      })
  }

  return (
    <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((star) => {
                const isFull = (hoverValue || localRating) >= star
                return (
                    <button
                        key={star}
                        disabled={readOnly || !userId}
                        onMouseEnter={() => !readOnly && setHoverValue(star)}
                        onMouseLeave={() => !readOnly && setHoverValue(0)}
                        onClick={() => handleRate(star)}
                        className={cn(
                            "transition-all duration-200", // Note: Removed 'focus:outline-none' to keep default a11y, style as needed
                            !readOnly && userId ? "cursor-pointer hover:scale-110" : "cursor-default",
                            isFull ? "text-yellow-400" : "text-zinc-600"
                        )}
                        aria-label={`Rate ${star} stars`}
                    >
                        <Star className={cn("w-4 h-4", isFull && "fill-current")} />
                    </button>
                )
            })}
        </div>
        <div className="text-[10px] text-zinc-500 font-medium">
             {averageRating.toFixed(1)} avg • ({totalRatings})
        </div>
    </div>
  )
}
